import { revalidateLogic } from '@tanstack/react-form'
import type { ReactNode } from 'react'
import z from 'zod'

import {
  formatRegionPrice,
  pluralizeUnit,
  productCategorySchema,
  productTypeSchema,
  REGIONS_BY_ID,
} from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { FormLayoutProvider } from '@/frontend/components/form/layouts/form-layout-context'
import { Button } from '@/frontend/components/ui/button'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useCreateProduct } from '@/frontend/hooks/api/use-create-product'
import { usePatchProduct } from '@/frontend/hooks/api/use-patch-product'

const PRODUCT_TYPES = [
  { label: 'Medication', value: 'medication' },
  { label: 'Vaccine', value: 'vaccine' },
  { label: 'Consumable', value: 'consumable' },
  { label: 'Service', value: 'service' },
] as const

/**
 * Client-side product form schema.
 *
 * Prices are held in minor currency units and rates in basis points, matching the API, so the
 * form's values are the request body. The inputs show pounds and percentages.
 */
const productSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    sku: z.string().min(1, 'SKU is required'),
    type: productTypeSchema,
    unit: z.string().min(1, 'Unit is required'),
    packSize: z.number({ message: 'Pack size is required' }).int().positive('Must be at least 1'),
    packUnit: z.string().min(1, 'Pack unit is required'),
    category: z.string().min(1, 'Category is required'),
    packCost: z.number({ message: 'Please provide COGS' }).int().nonnegative(),
    packFixedPrice: z.number().int().nonnegative().nullable(),
    markupBps: z.number().int().nonnegative().nullable(),
    vatBps: z.number({ message: 'Please provide VAT rate' }).int().nonnegative(),
    manufacturer: z.string(),
    isControlledSubstance: z.boolean(),
    fleaWormTick: z.boolean(),
    lowStockThreshold: z.number({ message: 'Please provide threshold' }).int().nonnegative(),
  })
  .superRefine((value, ctx) => {
    if (value.packFixedPrice != null || value.markupBps != null) return
    for (const path of ['packFixedPrice', 'markupBps'] as const) {
      ctx.addIssue({ code: 'custom', path: [path], message: 'Set either a fixed price or markup.' })
    }
  })

export type ProductFormValues = z.infer<typeof productSchema>

type ProductFormProps = {
  defaultValues?: ProductFormValues
  onSuccess?: () => void
  onCancel?: () => void
} & ({ productId?: undefined } | { productId: string })

const defaultProductValues = {
  name: '',
  sku: '',
  type: 'medication',
  unit: 'tablet',
  packSize: 1,
  packUnit: 'pack',
  category: '',
  packFixedPrice: null,
  markupBps: 0,
  vatBps: 2000,
  manufacturer: '',
  isControlledSubstance: false,
  fleaWormTick: false,
  lowStockThreshold: 10,
} as ProductFormValues

/**
 * Unified product form for creating and editing products.
 * When `productId` is provided the form operates in edit mode — submission
 * PATCHes the existing record. Otherwise it POSTs a new product.
 */
export function ProductForm({
  defaultValues = defaultProductValues,
  productId,
  onSuccess,
  onCancel,
}: ProductFormProps) {
  const { mutateAsync: createProduct } = useCreateProduct()
  const { mutateAsync: patchProduct } = usePatchProduct()
  const { region } = useViewerRegion()

  const isEditing = productId !== undefined

  const form = useAppForm({
    defaultValues,
    // Validate on submit first, then revalidate on every change so a cross-field error clears as
    // soon as either of the fields it spans is edited.
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: productSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        const payload = productSchema.parse(value)
        if (isEditing) {
          await patchProduct({ id: productId, ...payload })
        } else {
          await createProduct(payload)
          form.reset()
        }
        onSuccess?.()
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to save product',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <FormLayoutProvider layout="stacked-fill">
          <div className="flex flex-col gap-6 py-2">
            <form.GlobalError />

            <form.AppField name="name">
              {(field) => (
                <field.InputField label="Product name" placeholder="Synulox 250mg" required />
              )}
            </form.AppField>

            <FormSection
              title="Classification"
              description={
                !isEditing
                  ? `Product will be created in region: ${region.toUpperCase()}`
                  : undefined
              }
            >
              <div className="flex gap-3">
                <form.AppField name="sku">
                  {(field) => (
                    <field.InputField
                      label="SKU"
                      placeholder="MED-SYNULOX-250"
                      disabled={isEditing}
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="type">
                  {(field) => (
                    <field.SelectField
                      label="Type"
                      placeholder="Select type"
                      items={PRODUCT_TYPES}
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="manufacturer">
                  {(field) => <field.InputField label="Manufacturer" placeholder="Zoetis" />}
                </form.AppField>
              </div>

              {/* Category flexes to fill; checkbox sits at intrinsic width, aligned to the input baseline. */}
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <form.AppField name="category">
                    {(field) => (
                      <field.SelectField
                        label="Category"
                        placeholder="Select category"
                        items={productCategorySchema.options.map((cat) => ({
                          label: cat,
                          value: cat,
                        }))}
                        required
                      />
                    )}
                  </form.AppField>
                </div>
                <div className="pb-2">
                  <form.AppField name="isControlledSubstance">
                    {(field) => <field.CheckboxField label="Controlled substance" />}
                  </form.AppField>
                </div>
                <div className="pb-2">
                  <form.AppField name="fleaWormTick">
                    {(field) => <field.CheckboxField label="Included in flea, worm & tick plan" />}
                  </form.AppField>
                </div>
              </div>
            </FormSection>

            <FormSection title="Packaging & inventory">
              <div className="flex gap-3">
                <form.AppField name="packUnit">
                  {(field) => (
                    <field.InputField
                      label="Pack Unit"
                      description="The unit we order from suppliers (e.g. box, pack, case)"
                      placeholder="box"
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="unit">
                  {(field) => (
                    <field.InputField
                      label="Dispensable unit"
                      description="The unit dispensed to a member"
                      placeholder="tablet"
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="packSize">
                  {(field) => (
                    <field.NumericInputField
                      label="Pack size"
                      description="Number of dispensable units in a pack"
                      placeholder="100"
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="lowStockThreshold">
                  {(field) => (
                    <field.NumericInputField label="Low stock warning" placeholder="10" required />
                  )}
                </form.AppField>
              </div>
            </FormSection>

            <FormSection title="Pricing">
              <div className="flex gap-3">
                <form.AppField name="packCost">
                  {(field) => (
                    <field.CurrencyField
                      symbol={REGIONS_BY_ID[region].currencySymbol}
                      label="COGS"
                      placeholder="0.00"
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="markupBps">
                  {(field) => (
                    <field.NumericInputField scale={2} label="Markup (%)" placeholder="20" />
                  )}
                </form.AppField>
                <form.AppField name="packFixedPrice">
                  {(field) => (
                    <field.CurrencyField
                      symbol={REGIONS_BY_ID[region].currencySymbol}
                      label="Fixed price"
                      description="When set, overrides COGS * markup based pricing"
                      placeholder="0.00"
                    />
                  )}
                </form.AppField>
                <form.AppField name="vatBps">
                  {(field) => (
                    <field.NumericInputField scale={2} label="VAT (%)" placeholder="20" required />
                  )}
                </form.AppField>
              </div>

              {/* Live-derived per-unit price. Mirrors the server-side
                  `computeUnitPrice` logic against raw form inputs so it
                  updates as the user types. Hidden until enough is set to
                  derive a meaningful value. */}
              <form.Subscribe
                selector={(s) => ({
                  packCost: s.values.packCost,
                  packFixedPrice: s.values.packFixedPrice,
                  markupBps: s.values.markupBps,
                  packSize: s.values.packSize,
                  packUnit: s.values.packUnit,
                  unit: s.values.unit,
                })}
              >
                {({ packCost, packFixedPrice, markupBps, packSize, packUnit, unit }) => {
                  if (
                    !packSize ||
                    !unit ||
                    packCost == null ||
                    (packFixedPrice == null && markupBps == null)
                  )
                    return null

                  const usesFixed = packFixedPrice != null
                  const markup = markupBps ?? 0
                  const packPricePence = usesFixed
                    ? packFixedPrice
                    : Math.round((packCost * (10000 + markup)) / 10000)
                  const unitPricePence = Math.round(packPricePence / packSize)

                  const packPart = usesFixed
                    ? `${formatRegionPrice(packPricePence, region)} per ${packUnit}`
                    : `${formatRegionPrice(packCost, region)} per ${packUnit} + ${(markup / 100).toFixed(0)}% markup`
                  const formula =
                    packSize > 1
                      ? `(${packPart}) ÷ ${packSize} ${pluralizeUnit(unit, packSize)} per ${packUnit}`
                      : packPart

                  return (
                    <div className="rounded-md border bg-muted/30 px-3 py-2">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        Price per {unit}
                        <span className="text-xs">
                          · via {usesFixed ? 'fixed price' : 'COGS × markup'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-medium">
                          {formatRegionPrice(unitPricePence, region)} / {unit}
                        </span>
                        <span className="text-sm italic text-muted-foreground">= {formula}</span>
                      </div>
                    </div>
                  )
                }}
              </form.Subscribe>
            </FormSection>
          </div>
        </FormLayoutProvider>

        <div className="flex items-center justify-end gap-x-2 pt-3">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton>{isEditing ? 'Save Changes' : 'Add Product'}</form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}

/** Lightweight visual grouping for related fields inside a longer form. */
function FormSection({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5 border-b pb-1.5">
        <h3 className="text-headline-sm">{title}</h3>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  )
}
